export type StartScreenPrompt = {
  label: string;
  prompt: string;
  icon: string;
};

// In development, use full backend URL. In production, use relative path or env var
// Default to relative path /api for production (via Caddy reverse proxy)
const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL ?? "/api";

/**
 * ChatKit still expects a domain key at runtime. Use any placeholder locally,
 * but register your production domain at
 * https://platform.openai.com/settings/organization/security/domain-allowlist
 * and deploy the real key.
 * Must be set via NEXT_PUBLIC_CHATKIT_API_DOMAIN_KEY environment variable.
 *
 * If accessing via mars.aisisters.ai, use the production domain key.
 * If accessing via localhost, use a placeholder key.
 */
export const CHATKIT_API_DOMAIN_KEY = (() => {
  const envKey = process.env.NEXT_PUBLIC_CHATKIT_API_DOMAIN_KEY;

  // If domain key is explicitly set, use it (this should always be set in production)
  if (envKey) {
    return envKey;
  }

  // Fallback: Auto-detect based on hostname (only if env var is not set)
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "mars.aisisters.ai" || hostname.includes("aisisters.ai")) {
      // Production domain detected but no env var set - this is an error
      console.error(
        "NEXT_PUBLIC_CHATKIT_API_DOMAIN_KEY must be set for production domain. " +
          "Register your domain at https://platform.openai.com/settings/organization/security/domain-allowlist"
      );
      // Still return a placeholder to prevent immediate crash, but it will fail verification
      return "domain_pk_mars_aisisters_ai";
    }
  }

  // Default to placeholder for localhost (only if env var is not set)
  return "domain_pk_localhost_dev";
})();

// Use Next.js API route as proxy to add authentication headers
// This route will forward requests to the backend with the JWT token
export const CHATKIT_API_URL =
  process.env.NEXT_PUBLIC_CHATKIT_API_URL ?? "/api/chatkit";

// Login API URL
export const LOGIN_API_URL = `${BACKEND_URL}/login`;

export const GREETING =
  process.env.NEXT_PUBLIC_GREETING ??
  "What's your innovative idea about? Drop me a first message and I'll guide you through";

export const STARTER_PROMPTS: StartScreenPrompt[] = [
  {
    label: "Say hello",
    prompt: "Hello !",
    icon: "lightbulb",
  },
];

export const THEME_STORAGE_KEY = "color-scheme";

// Attachments configuration
export const ATTACHMENTS_ENABLED =
  process.env.NEXT_PUBLIC_ATTACHMENTS_ENABLED === "true" ||
  process.env.NEXT_PUBLIC_ATTACHMENTS_ENABLED === undefined;

export const ATTACHMENTS_MAX_COUNT = parseInt(
  process.env.NEXT_PUBLIC_ATTACHMENTS_MAX_COUNT ?? "10",
  10
);

export const ATTACHMENTS_MAX_SIZE = parseInt(
  process.env.NEXT_PUBLIC_ATTACHMENTS_MAX_SIZE ?? String(10 * 1024 * 1024),
  10
);

export const ATTACHMENTS_ACCEPT_IMAGES = (
  process.env.NEXT_PUBLIC_ATTACHMENTS_ACCEPT_IMAGES ??
  ".png,.jpeg,.jpg,.gif,.webp"
).split(",");

export const ATTACHMENTS_ACCEPT_PDF = (
  process.env.NEXT_PUBLIC_ATTACHMENTS_ACCEPT_PDF ?? ".pdf"
).split(",");

export const COMPOSER_PLACEHOLDER =
  process.env.NEXT_PUBLIC_COMPOSER_PLACEHOLDER ?? "Ask the assistant...";
